"use client";

import { usePathname } from "next/navigation";
import { useContext, useEffect } from "react";
import { InitialContext } from "../provider/initial-context-provider";
import publishEventToSDK from "./publish-event-to-sdk";
import {
  MIN_SDK_VERSION_FOR_TAB_BAR,
  sdkSupportsTabBar,
  semverGte,
} from "./sdk-capabilities";

export { sdkSupportsTabBar };

/**
 * Route prefix → whether the native tab bar should show on that screen.
 * true = home-like; false = flow/modal. Shared across brands; /home is
 * intentionally omitted so the home-wizard reducer owns that state.
 */
const ROUTE_TAB_BAR_VISIBILITY: Record<string, boolean> = {
  // /home is intentionally omitted — the home-wizard reducer owns tab bar
  // visibility while on /home (accounts for menu open, sub-steps, etc.)
  "/paywall": false,
  "/intake": false,
  "/visit": false,
  "/select-patient": false,
  "/block-visit": false,
  "/error": true,
};

/**
 * Owlet's intake and visit screens (Blueberry iframe) are laid out to coexist
 * with the native tab bar via `--owlet-tab-bar-offset`, so we skip the
 * route-driven hide publish for these paths on owlet.
 */
const IS_OWLET = process.env.NEXT_PUBLIC_BRAND === "owlet";
const OWLET_TAB_BAR_KEEP_VISIBLE = new Set(["/intake", "/visit"]);

/**
 * Hook that checks if the current SDK version meets a minimum threshold.
 * Uses raw useContext instead of useInitialContext to avoid Suspense — this
 * hook runs inside FeltEmbeddedComponent which is outside a Suspense boundary.
 */
function useSdkVersionCheck(minVersion: string): {
  supported: boolean;
  sdkVersion: string;
} {
  const { data: initialContext } = useContext(InitialContext);
  const sdkVersion = initialContext?.app?.sdkVersion ?? "0";
  return { supported: semverGte(sdkVersion, minVersion), sdkVersion };
}

/** Publishes tab bar visibility when the SDK version supports it; no-op otherwise. */
export function publishTabBarVisibility(
  sdkVersion: string,
  state: "hidden" | "visible"
): void {
  if (!sdkSupportsTabBar(sdkVersion)) {
    console.warn(
      `SDK version ${sdkVersion} does not support tab bar visibility`
    );
    return;
  }

  publishEventToSDK({
    topic: "tabBarVisibility",
    data: { state },
  });
}

/**
 * Syncs tab bar visibility automatically based on the current route pathname.
 */
export const useTabBarRouteSyncPublisher = (): void => {
  const pathname = usePathname();
  const { sdkVersion } = useSdkVersionCheck(MIN_SDK_VERSION_FOR_TAB_BAR);

  useEffect(() => {
    const basePath = Object.keys(ROUTE_TAB_BAR_VISIBILITY).find((key) =>
      pathname.startsWith(key)
    );
    // Only fire for routes explicitly in the map.
    // /home is omitted — the home-wizard reducer owns tab bar visibility there.
    if (basePath !== undefined) {
      if (IS_OWLET && OWLET_TAB_BAR_KEEP_VISIBLE.has(basePath)) return;
      const visible = ROUTE_TAB_BAR_VISIBILITY[basePath];
      publishTabBarVisibility(sdkVersion, visible ? "visible" : "hidden");
    }
  }, [pathname, sdkVersion]);
};

/** Publishes tab bar visibility to the SDK whenever the given state changes. */
export function useTabBarVisibility(state: "hidden" | "visible"): void {
  const { sdkVersion } = useSdkVersionCheck(MIN_SDK_VERSION_FOR_TAB_BAR);

  useEffect(() => {
    publishTabBarVisibility(sdkVersion, state);
  }, [sdkVersion, state]);
}
